import React from 'react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';

const GdprPage = () => {
  const { language } = useLanguage();

  const content = {
    da: {
      seoTitle: "Privatlivspolitik | Alora Rejser",
      seoDesc: "Læs Alora Rejsers privatlivspolitik og GDPR-betingelser. Vi passer godt på dine personoplysninger og helbredsoplysninger, når du booker en rejse.",
      title: "Privatlivspolitik",
      subtitle: "Alora Rejser",
      intro1: "Hos Alora Rejser er vores fornemmeste opgave at give dig “ro i maven” før, under og efter din ferie. Denne tryghed gælder i høj grad også vores behandling af dine personoplysninger. Fordi vi arrangerer rejser med særligt fokus på tilgængelighed, har vi brug for mere detaljerede oplysninger om dig end et traditionelt rejsebureau, herunder oplysninger om dit helbred og dine hjælpemidler.",
      intro2: "I denne privatlivspolitik beskriver vi fyldestgørende og gennemsigtigt, hvordan vi indsamler, behandler, videregiver og beskytter dine personoplysninger, samt hvilke rettigheder du har i den forbindelse.",
      
      sec1Title: "1. Dataansvarlig og kontaktoplysninger",
      sec1Text1: "Alora Rejser er dataansvarlig for behandlingen af de personoplysninger, som vi indsamler om dig i forbindelse med din bestilling og rejse.",
      sec1Text2: "Hvis du har spørgsmål til vores behandling af dine oplysninger, eller hvis du ønsker at gøre brug af dine rettigheder, kan du kontakte os her:",
      
      sec2Title: "2. Hvilke personoplysninger indsamler vi?",
      sec2Text1: "For at kunne levere din rejse og sikre, at alle dine behov imødekommes, indsamler og behandler vi følgende kategorier af personoplysninger:",
      
      sec2Card1Title: "A. Almindelige personoplysninger:",
      sec2Card1Items: [
        { label: "Identifikationsoplysninger:", desc: "Fulde navn, nøjagtigt som angivet i dit pas, køn og fødselsdato." },
        { label: "Kontaktoplysninger:", desc: "Adresse, telefonnummer og e-mailadresse." },
        { label: "Rejsedokumenter:", desc: "Pasnummer, udstedelsesdato, udløbsdato og nationalitet." },
        { label: "Økonomiske oplysninger:", desc: "Betalingsoplysninger og faktureringshistorik." },
        { label: "Rejseoplysninger:", desc: "Dine bestilte destinationer, rejsedatoer og eventuelle særønsker til flysæder eller diæter." }
      ],
      
      sec2Card2Title: "B. Særlige kategorier af personoplysninger (følsomme oplysninger):",
      sec2Card2Intro: "Da Alora Rejser specialiserer sig i rejser for personer med behov for tilgængelighed, indsamler vi helbredsoplysninger og oplysninger om funktionsnedsættelser for at kunne planlægge din rejse forsvarligt.",
      sec2Card2Items: [
        { label: "Helbred og diagnoser:", desc: "Oplysninger om dit specifikke fysiske handicap eller dine diagnoser." },
        { label: "Hjælpemidlers specifikationer:", desc: "Nøjagtige mål, mærke, højde, bredde, længde og vægt, på medbragte hjælpemidler som f.eks. manuelle kørestole eller el-scootere, da dette er et ultimativt krav for flyselskabernes sikkerhedsgodkendelse." },
        { label: "Behov på destinationen:", desc: "Behov for hjælpemidler på destinationen, f.eks. sengeheste, el-seng, toiletstol eller lift." },
        { label: "Pleje og assistance:", desc: "Behov for personlig pleje, ledsager eller medicinering under rejsen." }
      ],
      
      sec3Title: "3. Formål og det juridiske grundlag",
      sec3Text1: "Vi behandler udelukkende dine personoplysninger til udtrykkeligt angivne og legitime formål. Vores retsgrundlag for behandlingen afhænger af oplysningernes karakter:",
      
      sec3Card1Title: "Opfyldelse af kontrakt, almindelige oplysninger:",
      sec3Card1Text: "Vi behandler dine almindelige personoplysninger for at kunne indgå og opfylde aftalen med dig om levering af den bestilte pakkerejse, herunder udstedelse af flybilletter og reservation af hotel.",
      sec3Card1Law: "Retsgrundlag: Databeskyttelsesforordningens artikel 6, stk. 1, litra b.",
      
      sec3Card2Title: "Udtrykkeligt samtykke, følsomme oplysninger:",
      sec3Card2Text: "Behandlingen af dine helbredsoplysninger og behov for hjælpemidler sker udelukkende på baggrund af dit udtrykkelige samtykke, som du afgiver i forbindelse med bestillingen. Dette samtykke er en forudsætning for, at vi kan booke specialindrettede værelser og få dine hjælpemidler godkendt af flyselskaberne.",
      sec3Card2Law: "Retsgrundlag: Databeskyttelsesforordningens artikel 9, stk. 2, litra a.",
      
      sec3Card3Title: "Retlig forpligtelse:",
      sec3Card3Text: "Vi opbevarer fakturaer og betalingsoplysninger for at overholde gældende dansk lovgivning, herunder Bogføringsloven, samt Pakkerejselovens regler om dokumentation.",
      sec3Card3Law: "Retsgrundlag: Databeskyttelsesforordningens artikel 6, stk. 1, litra c.",
      
      sec3Card4Title: "Legitim interesse, markedsføring og evaluering:",
      sec3Card4Text: "Vi kan behandle almindelige kundeoplysninger til at sende dig evalueringsskemaer efter rejsen for at forbedre vores service. Bruger vi situationsbilleder fra rejser, hvor du optræder, til vores markedsføring, indhenter vi altid særskilt samtykke til dette.",
      sec3Card4Law: "Retsgrundlag: Databeskyttelsesforordningens artikel 6, stk. 1, litra f.",
      
      sec4Title: "4. Hvem videregiver vi dine oplysninger til?",
      sec4Text1: "For at din rejse overhovedet kan gennemføres, er det en nødvendighed, at vi videregiver relevante dele af dine personoplysninger til vores underleverandører og samarbejdspartnere. Vi deler kun de oplysninger, der er strengt nødvendige for den enkelte leverandør:",
      sec4Items: [
        "Flyselskaber: Navn, pasoplysninger og de præcise mål/vægt på dine hjælpemidler.",
        "Hoteller og overnatningssteder: Navn, datoer og specifikke krav til tilgængelighed på værelset.",
        "Lokale agenter og transportselskaber: Navn og behov for specialtransport, f.eks. liftbus, på destinationen.",
        "Offentlige myndigheder: I tilfælde af lovkrav, f.eks. krav om forhåndsregistrering af passagerer ved indrejse i visse lande (Advanced Passenger Information).",
        "IT-leverandører, databehandlere: Vi benytter eksterne IT-systemer, f.eks. bookingsystemer og e-mailudbydere, som behandler data på vores vegne. Disse er underlagt strenge databehandleraftaler, som sikrer, at de overholder GDPR."
      ],
      
      sec5Title: "5. Overførsel til tredjelande",
      sec5Text1: "Rejser du til en destination uden for EU/EØS, et såkaldt tredjeland, indebærer dette i sagens natur, at vi skal overføre dine oplysninger til flyselskaber og hoteller i det pågældende land.",
      sec5Text2: "Beskyttelsesniveauet i lande uden for EU/EØS er ikke altid det samme som i Danmark. Vi overfører dine oplysninger på et af følgende juridiske grundlag:",
      sec5Items: [
        "EU-Kommissionens tilstrækkelighedsvurdering: Landet er officielt anerkendt af EU-Kommissionen som havende et tilstrækkeligt databeskyttelsesniveau.",
        "Nødvendigt for opfyldelse af kontrakt, undtagelse: Hvis landet ikke har et tilstrækkeligt beskyttelsesniveau, overfører vi udelukkende dine oplysninger til leverandøren i tredjelandet, fordi overførslen er strengt nødvendig for, at vi kan opfylde vores aftale med dig om at levere den bestilte rejse. Retsgrundlag: Databeskyttelsesforordningens artikel 49, stk. 1, litra b."
      ],
      
      sec6Title: "6. Sikkerhed – Sådan passer vi på dine data",
      sec6Text1: "Vi har implementeret høje tekniske og organisatoriske sikkerhedsforanstaltninger for at beskytte dine personoplysninger mod hændelig eller ulovlig tilintetgørelse, tab, ændring samt uautoriseret adgang. Dette er særligt vigtigt for os, da vi håndterer følsomme helbredsoplysninger. Vores systemer er sikret med adgangskontrol, og kun medarbejdere, der har et reelt arbejdsbetinget behov, har adgang til dine følsomme oplysninger.",
      
      sec7Title: "7. Opbevaring og sletning",
      sec7Text1: "Vi opbevarer kun dine personoplysninger i det tidsrum, der er nødvendigt for at opfylde de formål, de blev indsamlet til:",
      sec7Items: [
        { title: "Følsomme oplysninger, handicap, hjælpemidler m.m.:", desc: "Disse slettes eller anonymiseres senest 6 måneder efter din hjemkomst, da de udelukkende bruges til afviklingen af den konkrete rejse. De kan dog opbevares længere, hvis der pågår en aktiv klagesag eller et forsikringskrav." },
        { title: "Regnskabsmateriale:", desc: "Fakturaer og betalingsdokumentation opbevares i indeværende regnskabsår plus 5 år for at overholde den danske Bogføringslov." },
        { title: "Almindelig kundehistorik:", desc: "Opbevares i op til 3 år efter seneste rejse med henblik på at kunne yde dig en bedre service ved din næste booking, hvorefter de slettes, medmindre du aktivt beder os beholde dem længere." }
      ],
      
      sec8Title: "8. Dine rettigheder",
      sec8Text1: "I henhold til Databeskyttelsesforordningen har du en række rettigheder i forhold til vores behandling af dine oplysninger:",
      sec8Items: [
        { title: "Ret til indsigt:", desc: "Du har ret til at få oplyst, præcis hvilke personoplysninger vi behandler om dig, og til hvilket formål." },
        { title: "Ret til berigtigelse:", desc: "Mener du, at de oplysninger, vi har om dig, er forkerte, f.eks. en stavefejl, der kan påvirke din flybillet, har du ret til at få dem rettet øjeblikkeligt." },
        { title: "Ret til sletning, “Retten til at blive glemt”:", desc: "Du kan i særlige tilfælde anmode om at få slettet dine personoplysninger, inden vores generelle slettefrister indtræffer." },
        { title: "Ret til begrænsning:", desc: "Du har i visse tilfælde ret til at få begrænset vores behandling af dine data til kun at bestå af opbevaring." },
        { title: "Ret til dataportabilitet:", desc: "Du har ret til at modtage de oplysninger, du selv har givet os, i et struktureret og maskinlæsbart format." },
        { title: "Ret til indsigelse:", desc: "Du har ret til at gøre indsigelse mod vores behandling af dine data, særligt hvis behandlingen sker med henblik på direkte markedsføring." },
        { title: "Ret til at trække samtykke tilbage:", desc: "Du kan til enhver tid trække dit udtrykkelige samtykke til behandlingen af dine følsomme helbredsoplysninger tilbage. Vigtigt: Trækker du samtykket tilbage forud for din rejse, vil Alora Rejser ikke længere lovligt kunne behandle kravene til din tilgængelighed. Dette vil medføre, at vi ikke kan gennemføre din rejse, hvilket vil blive betragtet som en afbestilling fra din side, underlagt de almindelige afbestillingsgebyrer." }
      ],
      
      sec9Title: "9. Automatiske afgørelser og profilering",
      sec9Text1: "Alora Rejser anvender ikke automatiserede individuelle afgørelser eller profilering, som f.eks. automatiske algoritmer til at hæve priser baseret på din adfærd eller til at afvise bookinger. Alle bookinger og vurderinger af tilgængelighed håndteres personligt af vores medarbejdere for at sikre den korrekte hjælp.",
      
      sec10Title: "10. Klage til Datatilsynet",
      sec10Text1: "Hvis du er utilfreds med den måde, vi behandler dine personoplysninger på, opfordrer vi dig til altid at kontakte os først.",
      sec10Text2: "Du har dog altid ret til at indgive en officiel klage til Datatilsynet, som er den nationale tilsynsmyndighed for databeskyttelse i Danmark. Datatilsynet kan kontaktes via deres hjemmeside: www.datatilsynet.dk."
    },
    en: {
      seoTitle: "Privacy Policy | Alora Rejser",
      seoDesc: "Read Alora Rejser's privacy policy and GDPR terms. We take good care of your personal information and health information when you book a trip.",
      title: "Privacy Policy",
      subtitle: "Alora Rejser",
      intro1: "At Alora Rejser, our primary task is to give you \"peace of mind\" before, during, and after your holiday. This security also applies to our treatment of your personal data. Because we arrange trips with a special focus on accessibility, we need more detailed information about you than a traditional travel agency, including information about your health and your assistive devices.",
      intro2: "In this privacy policy, we describe fully and transparently how we collect, process, disclose, and protect your personal data, as well as what rights you have in this connection.",
      
      sec1Title: "1. Data Controller and Contact Information",
      sec1Text1: "Alora Rejser is the data controller for the processing of the personal data we collect about you in connection with your booking and trip.",
      sec1Text2: "If you have questions about our processing of your information, or if you wish to exercise your rights, you can contact us here:",
      
      sec2Title: "2. What personal data do we collect?",
      sec2Text1: "To deliver your trip and ensure that all your needs are met, we collect and process the following categories of personal data:",
      
      sec2Card1Title: "A. General personal data:",
      sec2Card1Items: [
        { label: "Identification data:", desc: "Full name, exactly as stated in your passport, gender, and date of birth." },
        { label: "Contact data:", desc: "Address, phone number, and email address." },
        { label: "Travel documents:", desc: "Passport number, issue date, expiry date, and nationality." },
        { label: "Financial data:", desc: "Payment information and billing history." },
        { label: "Travel data:", desc: "Your booked destinations, travel dates, and any special requests for flight seats or diets." }
      ],
      
      sec2Card2Title: "B. Special categories of personal data (sensitive data):",
      sec2Card2Intro: "Since Alora Rejser specializes in travel for people with accessibility needs, we collect health information and information about disabilities to plan your trip safely.",
      sec2Card2Items: [
        { label: "Health and diagnoses:", desc: "Information about your specific physical disability or your diagnoses." },
        { label: "Assistive devices specifications:", desc: "Exact dimensions, brand, height, width, length, and weight of brought assistive devices such as manual wheelchairs or electric scooters, as this is an ultimate requirement for airlines' safety approval." },
        { label: "Needs at destination:", desc: "Need for assistive devices at the destination, e.g., bed rails, electric bed, toilet chair, or lift." },
        { label: "Care and assistance:", desc: "Need for personal care, companion, or medication during the trip." }
      ],
      
      sec3Title: "3. Purpose and legal basis",
      sec3Text1: "We process your personal data solely for explicitly stated and legitimate purposes. Our legal basis for processing depends on the nature of the information:",
      
      sec3Card1Title: "Performance of contract, general information:",
      sec3Card1Text: "We process your general personal data to enter into and fulfill the agreement with you regarding the delivery of the booked package tour, including issuing flight tickets and reserving hotels.",
      sec3Card1Law: "Legal basis: GDPR Article 6(1)(b).",
      
      sec3Card2Title: "Explicit consent, sensitive information:",
      sec3Card2Text: "The processing of your health information and need for assistive devices takes place solely on the basis of your explicit consent, which you provide in connection with booking. This consent is a prerequisite for us to book specially adapted rooms and get your assistive devices approved by the airlines.",
      sec3Card2Law: "Legal basis: GDPR Article 9(2)(a).",
      
      sec3Card3Title: "Legal obligation:",
      sec3Card3Text: "We store invoices and payment information to comply with applicable Danish legislation, including the Bookkeeping Act, as well as the Package Travel Act's rules on documentation.",
      sec3Card3Law: "Legal basis: GDPR Article 6(1)(c).",
      
      sec3Card4Title: "Legitimate interest, marketing and evaluation:",
      sec3Card4Text: "We may process general customer information to send you evaluation forms after the trip to improve our service. If we use situational photos from trips where you appear for our marketing, we always obtain separate consent for this.",
      sec3Card4Law: "Legal basis: GDPR Article 6(1)(f).",
      
      sec4Title: "4. Who do we disclose your information to?",
      sec4Text1: "For your trip to be carried out at all, it is a necessity that we disclose relevant parts of your personal data to our subcontractors and partners. We only share the information that is strictly necessary for the individual supplier:",
      sec4Items: [
        "Airlines: Name, passport details, and exact dimensions/weight of your assistive devices.",
        "Hotels and accommodations: Name, dates, and specific requirements for accessibility in the room.",
        "Local agents and transport companies: Name and need for special transport, e.g., lift bus, at the destination.",
        "Public authorities: In case of legal requirements, e.g., requirements for pre-registration of passengers upon entry into certain countries (Advanced Passenger Information).",
        "IT providers, data processors: We use external IT systems, e.g., booking systems and email providers, which process data on our behalf. These are subject to strict data processing agreements that ensure they comply with GDPR."
      ],
      
      sec5Title: "5. Transfer to third countries",
      sec5Text1: "If you travel to a destination outside the EU/EEA, a so-called third country, this naturally implies that we must transfer your information to airlines and hotels in the country in question.",
      sec5Text2: "The rules for data protection in countries outside the EU/EEA are not always as strict as in Denmark. We transfer your information on one of the following legal bases:",
      sec5Items: [
        "EU Commission's adequacy decision: The country is officially recognized by the EU Commission as having an adequate level of data protection.",
        "Necessary for performance of contract, exception: If the country does not have an adequate level of protection, we transfer your information to the supplier in the third country solely because the transfer is strictly necessary for us to fulfill our agreement with you to deliver the booked trip. Legal basis: GDPR Article 49(1)(b)."
      ],
      
      sec6Title: "6. Security – How we protect your data",
      sec6Text1: "We have implemented high technical and organizational security measures to protect your personal data against accidental or unlawful destruction, loss, alteration, and unauthorized access. This is particularly important to us as we handle sensitive health information. Our systems are secured with access control, and only employees who have a real work-related need have access to your sensitive information.",
      
      sec7Title: "7. Storage and deletion",
      sec7Text1: "We only store your personal data for the period necessary to fulfill the purposes for which they were collected:",
      sec7Items: [
        { title: "Sensitive information, disability, assistive devices, etc.:", desc: "These are deleted or anonymized no later than 6 months after your return, as they are solely used for the execution of the specific trip. However, they may be stored longer if an active complaint case or insurance claim is ongoing." },
        { title: "Accounting material:", desc: "Invoices and payment documentation are stored for the current financial year plus 5 years to comply with the Danish Bookkeeping Act." },
        { title: "General customer history:", desc: "Stored for up to 3 years after the latest trip with a view to being able to provide you with a better service at your next booking, after which they are deleted unless you actively ask us to keep them longer." }
      ],
      
      sec8Title: "8. Your rights",
      sec8Text1: "Under the General Data Protection Regulation, you have a number of rights in relation to our processing of your information:",
      sec8Items: [
        { title: "Right of access:", desc: "You have the right to be informed exactly what personal data we process about you and for what purpose." },
        { title: "Right to rectification:", desc: "If you believe that the information we have about you is incorrect, e.g., a typo that can affect your flight ticket, you have the right to have it corrected immediately." },
        { title: "Right to deletion, \"Right to be forgotten\":", desc: "In special cases, you can request to have your personal data deleted before our general deletion deadlines occur." },
        { title: "Right to restriction:", desc: "In certain cases, you have the right to have our processing of your data restricted to consist only of storage." },
        { title: "Right to data portability:", desc: "You have the right to receive the information you have given us in a structured and machine-readable format." },
        { title: "Right to object:", desc: "You have the right to object to our processing of your data, particularly if the processing takes place with a view to direct marketing." },
        { title: "Right to withdraw consent:", desc: "You can at any time withdraw your explicit consent to the processing of your sensitive health information. Important: If you withdraw consent prior to your trip, Alora Rejser will no longer legally be able to process the requirements for your accessibility. This will mean that we cannot carry out your trip, which will be considered a cancellation from your side, subject to standard cancellation fees." }
      ],
      
      sec9Title: "9. Automated decisions and profiling",
      sec9Text1: "Alora Rejser does not use automated individual decisions or profiling, such as automatic algorithms to raise prices based on your behavior or to reject bookings. All bookings and assessments of accessibility are handled personally by our employees to ensure correct help.",
      
      sec10Title: "10. Complaint to the Data Protection Authority",
      sec10Text1: "If you are dissatisfied with the way we process your personal data, we encourage you to always contact us first.",
      sec10Text2: "However, you always have the right to lodge an official complaint with the Danish Data Protection Authority (Datatilsynet), which is the national supervisory authority for data protection in Denmark. Datatilsynet can be contacted via their website: www.datatilsynet.dk."
    }
  };

  const t = content[language] || content.da;

  return (
    <div className="pt-32 pb-20 px-4 min-h-screen bg-white relative">
      <SEO 
        title={t.seoTitle} 
        description={t.seoDesc}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="border-b border-stone-200 pb-8 mb-12">
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#99A18C] mb-6">
            {t.title}
          </h1>
          <p className="text-lg text-stone-600 font-medium font-serif italic">
            {t.subtitle}
          </p>
        </div>

        {/* Intro Text */}
        <div className="space-y-6 text-slate-700 leading-relaxed text-base md:text-lg mb-12">
          <p>{t.intro1}</p>
          <p>{t.intro2}</p>
        </div>

        {/* Privacy Sections */}
        <div className="space-y-12 text-slate-700 leading-relaxed">

          {/* Section 1 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec1Title}
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                <strong className="text-slate-800">1.1.</strong> {t.sec1Text1}
              </p>
              <p>
                <strong className="text-slate-800">1.2.</strong> {t.sec1Text2}
              </p>
              
              <div className="mt-4 bg-white p-6 rounded-xl border border-stone-200/60 shadow-sm space-y-2">
                <p className="font-serif font-bold text-[#99A18C] text-lg">Alora Rejser</p>
                <p className="text-sm md:text-base"><strong className="text-slate-800">{language === 'da' ? 'Adresse:' : 'Address:'}</strong> Gjessøvej 56, 8600 Silkeborg</p>
                <p className="text-sm md:text-base"><strong className="text-slate-800">CVR-nr.:</strong> 46391772</p>
                <p className="text-sm md:text-base"><strong className="text-slate-800">E-mail:</strong> <a href="mailto:info@alorarejser.dk" className="text-[#99A18C] hover:underline">info@alorarejser.dk</a></p>
                <p className="text-sm md:text-base"><strong className="text-slate-800">{language === 'da' ? 'Telefon:' : 'Phone:'}</strong> <a href="tel:+4540500780" className="text-[#99A18C] hover:underline">+45 40 50 07 80</a></p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec2Title}
            </h2>
            <div className="space-y-6 text-slate-600">
              <p>
                <strong className="text-slate-800">2.1.</strong> {t.sec2Text1}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Category A */}
                <div className="bg-white p-6 rounded-xl border border-stone-200/60 shadow-sm">
                  <h3 className="font-serif font-bold text-[#99A18C] text-lg mb-4">
                    {t.sec2Card1Title}
                  </h3>
                  <ul className="space-y-4 text-sm md:text-base">
                    {t.sec2Card1Items.map((item, idx) => (
                      <li key={idx}>
                        <strong className="text-slate-800 block">{item.label}</strong>
                        {item.desc}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Category B */}
                <div className="bg-white p-6 rounded-xl border border-stone-200/60 shadow-sm">
                  <h3 className="font-serif font-bold text-[#99A18C] text-lg mb-4">
                    {t.sec2Card2Title}
                  </h3>
                  <p className="text-xs md:text-sm text-slate-500 mb-4">
                    {t.sec2Card2Intro}
                  </p>
                  <ul className="space-y-4 text-sm md:text-base">
                    {t.sec2Card2Items.map((item, idx) => (
                      <li key={idx}>
                        <strong className="text-slate-800 block">{item.label}</strong>
                        {item.desc}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec3Title}
            </h2>
            <div className="space-y-6 text-slate-600">
              <p>
                <strong className="text-slate-800">3.1.</strong> {t.sec3Text1}
              </p>

              <ul className="space-y-4 pl-2">
                <li className="bg-white p-5 rounded-xl border border-stone-200/60 shadow-sm">
                  <h3 className="font-serif font-bold text-[#99A18C] text-base md:text-lg mb-2">{t.sec3Card1Title}</h3>
                  <p className="text-sm md:text-base mb-2">
                    {t.sec3Card1Text}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-slate-500">
                    {t.sec3Card1Law}
                  </p>
                </li>

                <li className="bg-white p-5 rounded-xl border border-stone-200/60 shadow-sm">
                  <h3 className="font-serif font-bold text-[#99A18C] text-base md:text-lg mb-2">{t.sec3Card2Title}</h3>
                  <p className="text-sm md:text-base mb-2">
                    {t.sec3Card2Text}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-slate-500">
                    {t.sec3Card2Law}
                  </p>
                </li>

                <li className="bg-white p-5 rounded-xl border border-stone-200/60 shadow-sm">
                  <h3 className="font-serif font-bold text-[#99A18C] text-base md:text-lg mb-2">{t.sec3Card3Title}</h3>
                  <p className="text-sm md:text-base mb-2">
                    {t.sec3Card3Text}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-slate-500">
                    {t.sec3Card3Law}
                  </p>
                </li>

                <li className="bg-white p-5 rounded-xl border border-stone-200/60 shadow-sm">
                  <h3 className="font-serif font-bold text-[#99A18C] text-base md:text-lg mb-2">{t.sec3Card4Title}</h3>
                  <p className="text-sm md:text-base mb-2">
                    {t.sec3Card4Text}
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-slate-500">
                    {t.sec3Card4Law}
                  </p>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec4Title}
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                <strong className="text-slate-800">4.1.</strong> {t.sec4Text1}
              </p>

              <div className="my-6 bg-white p-6 rounded-xl border border-stone-200/60 shadow-sm">
                <ul className="list-disc pl-5 space-y-3">
                  {t.sec4Items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec5Title}
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                <strong className="text-slate-800">5.1.</strong> {t.sec5Text1}
              </p>
              <p>
                <strong className="text-slate-800">5.2.</strong> {t.sec5Text2}
              </p>

              <ul className="list-disc pl-5 space-y-3">
                {t.sec5Items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec6Title}
            </h2>
            <p className="text-slate-600">
              <strong className="text-slate-800">6.1.</strong> {t.sec6Text1}
            </p>
          </section>

          {/* Section 7 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec7Title}
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                <strong className="text-slate-800">7.1.</strong> {t.sec7Text1}
              </p>

              <div className="my-6 bg-white p-6 rounded-xl border border-stone-200/60 shadow-sm">
                <ul className="list-disc pl-5 space-y-4">
                  {t.sec7Items.map((item, idx) => (
                    <li key={idx}>
                      <strong className="text-slate-800 block">{item.title}</strong>
                      {item.desc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec8Title}
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                <strong className="text-slate-800">8.1.</strong> {t.sec8Text1}
              </p>

              <div className="my-6 bg-white p-6 rounded-xl border border-stone-200/60 shadow-sm">
                <ul className="list-disc pl-5 space-y-4">
                  {t.sec8Items.map((item, idx) => (
                    <li key={idx}>
                      <strong className="text-slate-800 block">{item.title}</strong>
                      {item.desc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec9Title}
            </h2>
            <p className="text-slate-600">
              <strong className="text-slate-800">9.1.</strong> {t.sec9Text1}
            </p>
          </section>

          {/* Section 10 */}
          <section className="bg-stone-50/50 p-6 md:p-8 rounded-2xl border border-stone-100 shadow-sm">
            <h2 className="text-xl md:text-2xl font-serif font-bold text-[#99A18C] mb-4">
              {t.sec10Title}
            </h2>
            <div className="space-y-4 text-slate-600">
              <p>
                <strong className="text-slate-800">10.1.</strong> {t.sec10Text1}
              </p>
              <p>
                <strong className="text-slate-800">10.2.</strong> {t.sec10Text2}
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default GdprPage;
